// Meta Ads | Demographics — AA report-page parity (gender + age donuts, the
// 4 account-level KPI tiles, a per-country breakdown table). Full-width
// section below the Paid Media two-up row (ClientDashboardPage), NOT a
// third half-width card squeezed next to MetaAdsPanel — AA's own layout
// puts 3 cards across at ~1000px+; this dashboard's Paid Media row is only
// ~540px per side, so this section gets its own full-width band instead.
//
// Map decision (raised to team-lead 2026-08-09): recharts has no
// choropleth and the palette is locked (no react-simple-maps/TopoJSON,
// no library swap) — AA's world-map card is DROPPED. Its only real
// information ("which of 5 countries had clicks") is already the country
// table below in full, so nothing is lost; two donuts side by side fill
// the freed width instead of squeezing for a third column.
//
// The bottom KPI row and both donut centers reuse `MetaAdsSection` directly
// (cost/impressions/clicks/avg_cpc/ctr + comparison.*_change_pct) — no new
// backend fields needed there; MetaAdsPanel (sibling card) reads the exact
// same object. Only `demographics` (by_gender/by_age/country_table) is new.

import type { ReactNode } from "react";
import type { MetaAdsCountryRow, MetaAdsSection } from "@/types/clientAnalytics";
import { SectionPanel } from "@/components/dashboard/SectionPanel";
import {
  NotConnected,
  SourceChip,
  StatBlock,
  ChangePctBadge,
  formatNum,
  formatMoney,
  formatPct,
  formatCompactNum,
  formatLocalDateRange,
} from "@/components/client-analytics/primitives";
import { DemographicDonut, buildGenderSlices, buildAgeSlices } from "@/components/client-analytics/frames/DemographicDonut";

// `row.country` IS the ISO-2 code (AA's raw payload has no separate
// display-name field — confirmed live by demo-backend) — this single
// helper both renders the flag and derives a readable name from it, so
// there's nothing to ask backend for on the display side.
function countryFlagEmoji(iso2: string): string | null {
  if (!/^[A-Za-z]{2}$/.test(iso2)) return null;
  const codePoints = [...iso2.toUpperCase()].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

let regionNames: Intl.DisplayNames | null | undefined;
function countryDisplayName(iso2: string): string {
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      regionNames = null; // unsupported runtime — fall back to the raw code below
    }
  }
  if (!regionNames || !/^[A-Za-z]{2}$/.test(iso2)) return iso2;
  return regionNames.of(iso2.toUpperCase()) ?? iso2;
}

// The caption used to hardcode "Last 90 days" -- coincidentally correct
// while the backfill window happened to be ~90 days, but not derived from
// anything real, so it would silently start lying the day that window
// changes (same failure class as an as-of-date caption the data doesn't
// actually guarantee). Derive it from demographics.period_start/period_end
// instead -- same "Mon D, YYYY – Mon D, YYYY" convention as every other date
// surface in this dashboard.
//
// Delegates to the shared primitives.formatLocalDateRange -- this file used
// to carry its own hand-rolled `parseLocalDate`/`formatDateRange` pair (a
// regex with no `$` anchor, so "2026-08-10garbage" parsed as if it were
// "2026-08-10"; no post-construction validity check, so "2026-02-31" rolled
// forward to Mar 3 instead of being rejected; and it only ever read the
// Y-M-D prefix, so a full timestamp's offset was silently discarded and
// re-anchored to local midnight -- the prod merge review flagged all three
// as a second, weaker implementation of the bug primitives.parseLocalDate
// already fixed for the other five surfaces). Deleted rather than patched:
// a second copy of this parser is exactly how it drifted out of sync with
// the shared one in the first place. Kept as a thin named export so this
// file's own call site below reads the same as before, and so
// dateRange.tz.test.ts can exercise it the same way it exercises
// DealPipelinePanel/CrmContactsFrame's formatDateRange.
export function formatDateRange(start?: string | null, end?: string | null): string | null {
  return formatLocalDateRange(start, end);
}

function CountryTable({ rows, currency }: { rows: MetaAdsCountryRow[]; currency: string }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-6 text-center text-dash-context text-muted-foreground">
        No country breakdown yet.
      </div>
    );
  }
  const sorted = [...rows].sort((a, b) => b.clicks - a.clicks);
  // No "Showing {n} of {m}" caption here (unlike the other tables in this
  // folder) -- `country_table` is never capped or paginated upstream
  // (map_meta_ads_demographics/mappers.py returns the full breakdown every
  // time), so the old caption's two numbers were ALWAYS identical: not a
  // real total, just `sorted.length` printed twice. That's the exact defect
  // this task set out to fix ("Showing {n} of {n} Rows" with no real total
  // behind either number) -- and for the 4 of 13 Meta clients with exactly
  // one country in demographics, "Showing 1 of 1" additionally read as a
  // loading failure rather than confirmation the pull completed (same
  // single-row framing problem MultiEngineRankTable.tsx fixes for Forestry
  // Rentals). Since no real total exists to wire in at any row count, the
  // caption is deleted outright rather than kept lying -- the table itself
  // (never hidden, per Carl's thin-data ruling) already states the true
  // row count implicitly via how many rows render.
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-dash-subtitle">
          <thead className="bg-muted">
            <tr className="border-b border-border">
              <th scope="col" className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Country
              </th>
              <th scope="col" className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Clicks
              </th>
              <th scope="col" className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Impressions
              </th>
              <th scope="col" className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                Average CPC
              </th>
              <th scope="col" className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                CTR
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const flag = countryFlagEmoji(row.country);
              return (
                <tr key={row.country} className="border-b border-border last:border-0 transition-colors hover:bg-muted/60">
                  <td className="px-4 py-2.5 font-medium text-foreground whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      {flag && <span aria-hidden="true">{flag}</span>}
                      {countryDisplayName(row.country)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular whitespace-nowrap">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <span className="text-foreground">{formatNum(row.clicks)}</span>
                      <ChangePctBadge pct={row.clicks_change_pct} />
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular whitespace-nowrap">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <span className="text-foreground">{formatNum(row.impressions)}</span>
                      <ChangePctBadge pct={row.impressions_change_pct} />
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular whitespace-nowrap">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <span className="text-foreground">{formatMoney(row.avg_cpc, currency, 2)}</span>
                      <ChangePctBadge pct={row.avg_cpc_change_pct} metric="avg_cpc" />
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular whitespace-nowrap">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <span className="text-foreground">{formatPct(row.ctr, 2)}</span>
                      <ChangePctBadge pct={row.ctr_change_pct} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DonutCard({
  title,
  clicksBadge,
  children,
}: {
  title: string;
  clicksBadge: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex-1 rounded-lg border border-border bg-card p-4 flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
        {clicksBadge}
      </div>
      {children}
    </div>
  );
}

export function MetaAdsDemographicsPanel({
  metaAds,
}: {
  metaAds?: MetaAdsSection | null;
}) {
  if (!metaAds) {
    return (
      <NotConnected
        message="Meta Ads Demographics — Facebook/Instagram advertising isn't running for this account"
        cta="Talk to your account manager"
      />
    );
  }

  const demo = metaAds.demographics;
  const hasAnyDemo =
    !!demo && (demo.by_gender.length > 0 || demo.by_age.length > 0 || demo.country_table.length > 0);
  if (!hasAnyDemo) {
    // Meta itself IS connected (metaAds exists — MetaAdsPanel already shows
    // its real numbers) but the audience-breakdown pull hasn't populated
    // for this client yet -- a distinct message from "Meta isn't running
    // at all" above, never a collapsed/blank card.
    return (
      <NotConnected message="Meta Ads Demographics — audience breakdown isn't available for this account yet" />
    );
  }

  // AA shows the SAME account-level clicks delta reused on all three top
  // cards (Gender/Map/Age), not a per-dimension delta the data can't
  // support -- see MetaAdsDemographicsPanel's module docstring. Same object
  // MetaAdsPanel's own Clicks tile reads.
  const clicksBadge = <ChangePctBadge pct={metaAds.comparison?.clicks_change_pct} />;

  const dateRange = formatDateRange(demo.period_start, demo.period_end);
  const caption = dateRange ? `${dateRange} · Meta Ads` : "Meta Ads";

  return (
    <SectionPanel
      title="Meta Ads | Demographics"
      caption={caption}
      action={<SourceChip label="Meta Ads" />}
    >
      <div className="p-5 flex flex-col gap-5">
        <div className="flex gap-4 items-stretch">
          <DonutCard title="Gender" clicksBadge={clicksBadge}>
            <DemographicDonut title="Gender" slices={buildGenderSlices(demo.by_gender)} />
          </DonutCard>
          <DonutCard title="Age" clicksBadge={clicksBadge}>
            <DemographicDonut title="Age" slices={buildAgeSlices(demo.by_age)} />
          </DonutCard>
        </div>

        <div className="flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
          <StatBlock
            label="Clicks"
            value={formatNum(metaAds.clicks)}
            badge={<ChangePctBadge pct={metaAds.comparison?.clicks_change_pct} />}
          />
          <StatBlock
            label="Impressions"
            value={formatCompactNum(metaAds.impressions)}
            badge={<ChangePctBadge pct={metaAds.comparison?.impressions_change_pct} />}
          />
          <StatBlock
            label="Average CPC"
            value={formatMoney(metaAds.avg_cpc, metaAds.currency, 2)}
            badge={<ChangePctBadge pct={metaAds.comparison?.avg_cpc_change_pct} metric="avg_cpc" />}
          />
          <StatBlock
            label="CTR"
            value={formatPct(metaAds.ctr, 2)}
            badge={<ChangePctBadge pct={metaAds.comparison?.ctr_change_pct} />}
          />
        </div>

        <CountryTable rows={demo.country_table} currency={metaAds.currency} />
      </div>
    </SectionPanel>
  );
}
